/**
 * Image Workflows API
 * Workflow management and execution endpoints
 */

import apiClient from './client';
import type {
  Workflow,
  WorkflowCreate,
  WorkflowUpdate,
  WorkflowListResponse,
  WorkflowExecution,
  ExecutionListResponse,
  ExecuteWorkflowRequest,
  ExecutionStartResponse,
  UserRun,
  WorkflowHistoryListResponse,
} from '@/types/imageWorkflow';
import type { NodeTypeRegistryResponse } from '@/types/workflowFields';

export const imageWorkflowsApi = {
  // ============================================================================
  // Node Type Registry
  // ============================================================================

  /**
   * Get all available workflow node types with their field specifications
   * Includes input/output field contracts and config field definitions
   */
  getNodeTypes: async (): Promise<NodeTypeRegistryResponse> => {
    const response = await apiClient.get<NodeTypeRegistryResponse>('/image-workflows/node-types');
    return response.data;
  },

  // ============================================================================
  // Workflow Definition CRUD
  // ============================================================================

  /**
   * List workflow definitions with optional filters
   */
  list: async (params?: {
    is_active?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<WorkflowListResponse> => {
    const response = await apiClient.get<WorkflowListResponse>('/image-workflows', { params });
    return response.data;
  },

  /**
   * Get a single workflow by ID
   */
  get: async (workflowId: string): Promise<Workflow> => {
    const response = await apiClient.get<Workflow>(`/image-workflows/${workflowId}`);
    return response.data;
  },

  /**
   * Create a new workflow
   */
  create: async (data: WorkflowCreate): Promise<Workflow> => {
    const response = await apiClient.post<Workflow>('/image-workflows', data);
    return response.data;
  },

  /**
   * Update a workflow
   */
  update: async (workflowId: string, data: WorkflowUpdate): Promise<Workflow> => {
    const response = await apiClient.put<Workflow>(`/image-workflows/${workflowId}`, data);
    return response.data;
  },

  /**
   * Delete (soft) a workflow
   */
  delete: async (workflowId: string): Promise<void> => {
    await apiClient.delete(`/image-workflows/${workflowId}`);
  },

  // ============================================================================
  // Workflow History
  // ============================================================================

  /**
   * Get version history for a workflow
   */
  getHistory: async (
    workflowId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<WorkflowHistoryListResponse> => {
    const response = await apiClient.get<WorkflowHistoryListResponse>(
      `/image-workflows/${workflowId}/history`,
      { params }
    );
    return response.data;
  },

  // ============================================================================
  // Workflow Execution
  // ============================================================================

  /**
   * Execute a workflow
   * Supports targeting multiple users. Execution runs in background.
   * Uses longer timeout (5 minutes) for image generation
   */
  execute: async (data: ExecuteWorkflowRequest): Promise<ExecutionStartResponse> => {
    const response = await apiClient.post<ExecutionStartResponse>(
      '/image-workflows/execute',
      data,
      { timeout: 300000 } // 5 minutes for image generation
    );
    return response.data;
  },

  /**
   * List workflow executions with optional filters
   */
  listExecutions: async (params?: {
    workflow_id?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<ExecutionListResponse> => {
    const response = await apiClient.get<ExecutionListResponse>('/image-workflows/executions', {
      params,
    });
    return response.data;
  },

  /**
   * Get execution details
   */
  getExecution: async (
    executionId: string,
    includeRuns: boolean = false
  ): Promise<WorkflowExecution> => {
    const response = await apiClient.get<WorkflowExecution>(
      `/image-workflows/executions/${executionId}`,
      { params: { include_runs: includeRuns } }
    );
    return response.data;
  },

  /**
   * Get user run details with optional steps
   */
  getUserRun: async (
    executionId: string,
    userRunId: string,
    includeSteps: boolean = false
  ): Promise<UserRun> => {
    const response = await apiClient.get<UserRun>(
      `/image-workflows/executions/${executionId}/runs/${userRunId}`,
      { params: { include_steps: includeSteps } }
    );
    return response.data;
  },

  /**
   * Resume a failed user run from a specific node
   * Reuses outputs from previously completed nodes
   */
  resumeUserRun: async (
    executionId: string,
    userRunId: string,
    options?: {
      resumeFromNodeKey?: string;
      sync?: boolean;
      timeoutSeconds?: number;
    }
  ): Promise<UserRun> => {
    const response = await apiClient.post<UserRun>(
      `/image-workflows/executions/${executionId}/runs/${userRunId}/resume`,
      {
        resume_from_node_key: options?.resumeFromNodeKey,
        sync: options?.sync ?? false,
        timeout_seconds: options?.timeoutSeconds ?? 300,
      },
      { timeout: (options?.timeoutSeconds ?? 300) * 1000 + 5000 } // Add 5s buffer
    );
    return response.data;
  },

  // ============================================================================
  // Polling Helpers
  // ============================================================================

  /**
   * Poll execution status until complete or timeout
   */
  pollExecution: async (
    executionId: string,
    options?: {
      interval?: number;
      timeout?: number;
      onUpdate?: (execution: WorkflowExecution) => void;
    }
  ): Promise<WorkflowExecution> => {
    const interval = options?.interval ?? 2000;
    const timeout = options?.timeout ?? 300000; // 5 minutes default
    const onUpdate = options?.onUpdate;

    const startTime = Date.now();

    while (true) {
      const execution = await imageWorkflowsApi.getExecution(executionId, true);

      if (onUpdate) {
        onUpdate(execution);
      }

      // Check if execution is complete
      if (['completed', 'failed', 'partial', 'cancelled'].includes(execution.status)) {
        return execution;
      }

      // Check timeout
      if (Date.now() - startTime > timeout) {
        throw new Error('Execution polling timeout');
      }

      // Wait before next poll
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  },
};

// ============================================================================
// Workflow Templates (helper functions)
// ============================================================================

export const workflowTemplates = {
  /**
   * Create an empty workflow definition (new dual-layer structure)
   */
  empty: (key: string = 'new_workflow'): WorkflowCreate => ({
    name: 'New Workflow',
    key,
    definition: {
      config: {
        nodes: [],
        edges: [],
      },
      canvas: {
        positions: {},
        viewport: { x: 0, y: 0, zoom: 1 },
      },
    },
    default_storage_mode: 'temp',
    prompt_strategy: 'agent',
  }),

  /**
   * Create a simple generate workflow (new dual-layer structure)
   */
  simpleGenerate: (name: string, key: string, prompt: string): WorkflowCreate => ({
    name,
    key,
    definition: {
      config: {
        nodes: [
          {
            key: 'generate',
            type: 'generate',
            data: {
              label: 'Generate Image',
              prompt,
              model: 'auto',
            },
          },
          {
            key: 'output',
            type: 'output',
            data: {
              label: 'Output',
              save_to_library: false,
            },
          },
        ],
        edges: [{ from: 'generate', to: 'output' }],
      },
      canvas: {
        positions: {
          generate: { x: 100, y: 100 },
          output: { x: 400, y: 100 },
        },
        viewport: { x: 0, y: 0, zoom: 1 },
      },
    },
    default_storage_mode: 'temp',
    prompt_strategy: 'agent',
  }),

  /**
   * Create a generate-and-edit workflow (new dual-layer structure)
   */
  generateAndEdit: (
    name: string,
    key: string,
    generatePrompt: string,
    editPrompt: string
  ): WorkflowCreate => ({
    name,
    key,
    definition: {
      config: {
        nodes: [
          {
            key: 'generate',
            type: 'generate',
            data: {
              label: 'Generate Base',
              prompt: generatePrompt,
              model: 'auto',
            },
          },
          {
            key: 'edit',
            type: 'edit',
            data: {
              label: 'Apply Edit',
              prompt: editPrompt,
            },
          },
          {
            key: 'output',
            type: 'output',
            data: {
              label: 'Save Result',
              save_to_library: true,
            },
          },
        ],
        edges: [
          { from: 'generate', to: 'edit' },
          { from: 'edit', to: 'output' },
        ],
      },
      canvas: {
        positions: {
          generate: { x: 100, y: 100 },
          edit: { x: 400, y: 100 },
          output: { x: 700, y: 100 },
        },
        viewport: { x: 0, y: 0, zoom: 1 },
      },
    },
    default_storage_mode: 'library',
    prompt_strategy: 'agent',
  }),
};
